/**
 * Utility functions for formatting coordinate values
 * This ensures consistent display of latitude and longitude across the application
 */

/**
 * Format coordinate values for display (5 decimal places)
 * @param {number|string} value - The coordinate value
 * @param {string} fieldName - The field name to check if it's a coordinate
 * @returns {string} - Formatted coordinate or original value
 */
export const formatCoordinateForDisplay = (value, fieldName) => {
    // Check if this is a coordinate field
    if (fieldName && (fieldName.toLowerCase().includes('latitude') || fieldName.toLowerCase().includes('longitude'))) {
        // If it's a number, format to 5 decimal places
        if (typeof value === 'number' || !isNaN(parseFloat(value))) {
            return parseFloat(value).toFixed(5);
        }
    }
    // Return original value for non-coordinate fields
    return value;
};

/**
 * Format a coordinate value to a specific number of decimal places
 * @param {number|string} value - The coordinate value
 * @param {number} decimalPlaces - Number of decimal places (default: 5)
 * @returns {string} - Formatted coordinate
 */
export const formatCoordinate = (value, decimalPlaces = 5) => {
    if (typeof value === 'number' || !isNaN(parseFloat(value))) {
        return parseFloat(value).toFixed(decimalPlaces);
    }
    return value;
};

/**
 * Check if a field name represents a coordinate field
 * @param {string} fieldName - The field name to check
 * @returns {boolean} - True if it's a coordinate field
 */
export const isCoordinateField = (fieldName) => {
    if (!fieldName) return false;
    return fieldName.toLowerCase().includes('latitude') || fieldName.toLowerCase().includes('longitude');
};

/**
 * Format coordinates in a more readable format (e.g., "40.7128° N, 74.0060° W")
 * @param {number} latitude - Latitude value
 * @param {number} longitude - Longitude value
 * @param {number} decimalPlaces - Number of decimal places (default: 5)
 * @returns {string} - Formatted coordinate string
 */
export const formatCoordinatesReadable = (latitude, longitude, decimalPlaces = 5) => {
    if (typeof latitude !== 'number' || typeof longitude !== 'number') {
        return `${latitude}, ${longitude}`;
    }
    
    const latDir = latitude >= 0 ? 'N' : 'S';
    const lngDir = longitude >= 0 ? 'E' : 'W';
    
    const latFormatted = Math.abs(latitude).toFixed(decimalPlaces);
    const lngFormatted = Math.abs(longitude).toFixed(decimalPlaces);
    
    return `${latFormatted}° ${latDir}, ${lngFormatted}° ${lngDir}`;
}; 